import { type ClusterValid } from '@qdrant/qdrant-cloud-public-api/cluster/v1';
import { useQuery } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { clusterDataService } from '@/api/services/cluster/cluster-data';
import { useClusterId } from '@/routes/_authenticated/_dashboard/accounts/$accountId/clusters/$clusterId/route.hooks';
import { useAccountId } from '@/routes/_authenticated/_dashboard/accounts/$accountId/route.hooks';
import { isClusterUndergoingChanges } from '@/utils/cluster-utils';

type CollectionsContextType = {
  availableCollections: string[];
  loadingCollections: boolean;
  errorLoadingCollections: boolean;
  refetchCollections: () => void;
  clusterIsNotReachable: boolean;
};

export const UNREACHABLE_CLUSTER_MSG =
  "Collections can't be retrieved while the cluster is unreachable due to creation or ongoing changes.";

export const CollectionsContext = createContext<CollectionsContextType | undefined>(undefined);

export const CollectionsProvider = ({ cluster, children }: { cluster: ClusterValid; children: ReactNode }) => {
  const clusterId = useClusterId();
  const accountId = useAccountId();

  const clusterMaybeNotReachable = isClusterUndergoingChanges(cluster);
  const {
    data: collectionsResponse,
    isFetching: loadingCollections,
    isError: errorLoadingCollections,
    refetch: refetchCollections,
  } = useQuery({
    // The query carries its own `UNAVAILABLE` retry policy: the cluster's endpoint can still be
    // unreachable right after a create or resume even though `isClusterUndergoingChanges` says
    // otherwise, so the key/token dialog gets a second attempt instead of dropping straight into its
    // error state. Genuine failures and rate limiting still surface via `errorLoadingCollections`.
    ...clusterDataService.listCollectionsQuery({ accountId, clusterId }),
    // Stale time is set to 0 to ensure that the collections are always up to date
    staleTime: 0,
    enabled: !clusterMaybeNotReachable,
  });

  const availableCollections = useMemo(
    () => (collectionsResponse ? collectionsResponse.map((collection) => collection.name) : []),
    [collectionsResponse],
  );

  const contextValue = useMemo(
    () => ({
      availableCollections,
      loadingCollections,
      errorLoadingCollections,
      refetchCollections,
      clusterIsNotReachable: clusterMaybeNotReachable,
    }),
    [availableCollections, loadingCollections, errorLoadingCollections, refetchCollections, clusterMaybeNotReachable],
  );

  return <CollectionsContext.Provider value={contextValue}>{children}</CollectionsContext.Provider>;
};

// Hook for using context
export const useCollections = () => {
  const context = useContext(CollectionsContext);
  if (!context) {
    throw new Error('useCollections must be used within a CollectionsProvider');
  }
  return context;
};
